import { Component, OnInit, ChangeDetectionStrategy, inject, DestroyRef, ChangeDetectorRef } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { WarehouseStatusView } from '@shared/components/analytics-tabs/warehouse-status/warehouse-status-view.component';
import { MissionPerformanceView } from '@shared/components/analytics-tabs/missions-performance/mission-performance-view.component';
import { TabItem } from '@shared/components/icam-tabs/icam-tabs.component';
import { DiagnosticView } from '@shared/components/analytics-tabs/diagnostic-view/diagnostic-view.component';
import { MachineInventoryView } from '@shared/components/analytics-tabs/machine-inventory-view/machine-inventory-view.component';
import { AnalyticsFiltersService } from '@shared/services/api/analytics-filters.service';
import { FilterOrSortDropdownType, FilterSelectedEventType, Roles, UserRole } from '@shared/models/types';
import { ProfileService } from '@shared/services/api/profile.service';
import { SmartEnergyView } from '@shared/components/analytics-tabs/smart-energy-view/smart-energy-view.component';

@Component({
  selector: 'app-analytics-page',
  templateUrl: './analytics-page.component.html',
  styleUrls: ['./analytics-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AnalyticsPageComponent implements OnInit {
  private _destroyRef = inject(DestroyRef);
  private _cdr = inject(ChangeDetectorRef);
  private profileService = inject(ProfileService);
  private analyticsFiltersService = inject(AnalyticsFiltersService);

  tabItems: TabItem[] = [
    { id: 'warehouse', label: 'ANALYTICS.TABS.WAREHOUSE_STATUS' },
    { id: 'mission', label: 'ANALYTICS.TABS.MISSION_PERFORMANCE' },
    { id: 'diagnostics', label: 'ANALYTICS.TABS.DIAGNOSTICS' },
    { id: 'machine-inventory', label: 'ANALYTICS.TABS.MACHINE_INVENTORY' },
    { id: 'smart-energy', label: 'ANALYTICS.TABS.SMART_ENERGY' }
  ];

  private tabComponentMap = new Map<string | number, any>([
    ['warehouse', WarehouseStatusView],
    ['mission', MissionPerformanceView],
    ['diagnostics', DiagnosticView],
    ['machine-inventory', MachineInventoryView],
    ['smart-energy', SmartEnergyView]
  ]);

  selectedTabId: string | number = this.tabItems[0]?.id || 0;

  get activeComponent(): any | null {
    return this.tabComponentMap.get(this.selectedTabId) || null;
  }

  filterFields: FilterOrSortDropdownType[] = [];
  initialFilters: any = {};
  private currentSelectedFilters: Record<string, any[]> = {};
  private userRole: UserRole | null = null;

  private filtersSubject = new BehaviorSubject<Record<string, any[]>>({});
  filters$ = this.filtersSubject.asObservable();

  ngOnInit() {
    this.profileService.role$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe({
      next: (role) => {
        this.userRole = role;
        this._cdr.markForCheck();
      }
    });

    this.loadFilters();
  }

  private loadFilters() {
    this.analyticsFiltersService.all$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe({
      next: (filterData) => {
        if (filterData) {
          this.filterFields = this.convertToFilterFields(filterData, this.currentSelectedFilters);
          this._cdr.markForCheck();
        }
      },
      error: (err) => {
        console.error('Error loading filters:', err);
      }
    });
  }
  private convertToFilterFields(
    filterData: any,
    selectedFilters: Record<string, any[]> = {}
  ): FilterOrSortDropdownType[] {
    if (!filterData) return [];

    const fields: FilterOrSortDropdownType[] = [];

    const filterKeys: string[] =
      this.userRole === Roles.ADMIN ? ['accountName', 'machineType', 'idMachine'] : ['machineType', 'idMachine'];

    filterKeys.forEach((key) => {
      if (filterData[key] && Array.isArray(filterData[key])) {
        fields.push({
          key,
          values: filterData[key].map((value: string) => ({
            id: value,
            value: value,
            selected: selectedFilters[key]?.includes(value) || false
          }))
        });
      }
    });

    fields.push({
      key: 'custom_period',
      values: [],
      initialValue: this.initialFilters?.custom_period || undefined
    });

    return fields;
  }

  onFiltersSelected(selectedFilters: FilterSelectedEventType[]) {
    const filters = this.buildFiltersFromSelection(selectedFilters);
    this.currentSelectedFilters = { ...filters };
    this.initialFilters = this.convertFiltersToInitialFormat(filters, selectedFilters);
    this.filtersSubject.next(filters);

    this.analyticsFiltersService.updateFilters(filters);
  }

  onResetFilters() {
    this.filtersSubject.next({});
    this.initialFilters = {};
    this.currentSelectedFilters = {};

    this.analyticsFiltersService.updateFilters({});
  }

  private buildFiltersFromSelection(selectedFilters: FilterSelectedEventType[]): Record<string, any[]> {
    const filters: Record<string, any[]> = {};

    selectedFilters.forEach((filter) => {
      if (!this.isValidFilter(filter)) return;

      if (filter.name === 'date_period') {
        const dateFilters = this.parseDatePeriod(filter.selectedValue);
        Object.assign(filters, dateFilters);
      } else {
        filters[filter.name] = [filter.selectedValue];
      }
    });

    Object.keys(filters).forEach((key) => {
      if (!filters[key] || filters[key].length === 0) {
        delete filters[key];
      }
    });

    return filters;
  }

  private isValidFilter(filter: FilterSelectedEventType): boolean {
    return (
      filter.operation === 'filter' &&
      filter.selectedValue !== null &&
      filter.selectedValue !== undefined &&
      filter.selectedValue !== '' &&
      (typeof filter.selectedValue !== 'string' || filter.selectedValue.trim() !== '')
    );
  }

  private parseDatePeriod(dateRange: string): Record<string, string[]> {
    const dates = dateRange.split(' - ');
    if (dates.length !== 2) return {};

    const endDateISO = dates[0].split('T')[0];
    const startDateISO = dates[1].split('T')[0];

    if (!startDateISO?.trim() || !endDateISO?.trim()) return {};

    return {
      startDate: [startDateISO],
      endDate: [endDateISO]
    };
  }

  private convertFiltersToInitialFormat(
    filters: Record<string, any[]>,
    selectedFilters?: FilterSelectedEventType[]
  ): any {
    const initialFormat: any = {};

    Object.keys(filters).forEach((key) => {
      const values = filters[key];
      if (values && values.length > 0) {
        initialFormat[key] = values[0];
      }
    });

    // Store the formatted date range for custom_period display
    if (selectedFilters) {
      const datePeriodFilter = selectedFilters.find((f) => f.name === 'date_period');
      if (datePeriodFilter && datePeriodFilter.text) {
        initialFormat['custom_period'] = datePeriodFilter.text;
      }
    }

    // If we have startDate and endDate but no custom_period set yet,
    // preserve the existing custom_period value
    if (!initialFormat['custom_period'] && filters['startDate'] && filters['endDate']) {
      if (this.initialFilters?.custom_period) {
        initialFormat['custom_period'] = this.initialFilters.custom_period;
      }
    }

    return initialFormat;
  }

  onTabChange(tab: TabItem) {
    if (!tab) return;
    this.selectedTabId = tab.id;
  }
}
